/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * Map a notification's parent_table (+ id) to an in-app detail route.
 *
 * Callers store parent_table inconsistently:
 * - assignment/activity notifications use lowercase plurals (`leads`, `deals`)
 * - mention (echo) notifications use capitalized singulars (`Lead`, `Case`)
 *
 * A missing map used to fall through to `/${Type}/detail/${id}`, which is not
 * a real route — React Router renders nothing and the page goes blank.
 */

/** Canonical path builders keyed by a normalized singular token. */
const DETAIL_PATH = {
  lead: (id) => `/leads/${id}`,
  deal: (id) => `/deals/${id}`,
  account: (id) => `/accounts/${id}`,
  contact: (id) => `/contacts/${id}`,
  case: (id) => `/cases/${id}`,
  order: (id) => `/orders/${id}`,
  invoice: (id) => `/invoices/${id}`,
  quote: (id) => `/quote/${id}`,
  quotation: (id) => `/quote/${id}`,
  product: (id) => `/products/${id}`,
  trip: (id) => `/trips/detail/${id}`,
  travel_order: (id) => `/travel-order/${id}`,
  // Activities/tasks have a calendar list, not a :id detail page.
  activity: () => `/tasks`,
  task: () => `/tasks`,
  user: (id) => `/users/${id}/profile`,
};

/** Aliases / plurals / historical spellings → singular key in DETAIL_PATH. */
const ALIASES = {
  leads: "lead",
  deals: "deal",
  accounts: "account",
  contacts: "contact",
  cases: "case",
  orders: "order",
  invoices: "invoice",
  quotes: "quote",
  quotations: "quotation",
  products: "product",
  trips: "trip",
  travel_orders: "travel_order",
  activities: "activity",
  tasks: "task",
  users: "user",
};

function normalizeParentTable(parentTable) {
  if (parentTable == null) return "";
  const raw = String(parentTable).trim().toLowerCase().replace(/[\s-]+/g, "_");
  if (!raw || raw === "echo") return raw;
  // Prefer exact keys (incl. singular "case") before stripping a trailing "s",
  // otherwise Case → "ca" and the redirect fails.
  if (ALIASES[raw]) return ALIASES[raw];
  if (DETAIL_PATH[raw]) return raw;
  return raw.replace(/s$/, "");
}

/**
 * @param {string|null|undefined} parentTable
 * @param {string|number|null|undefined} parentId
 * @returns {string|null} path to navigate to, or null when unknown / incomplete
 */
export function getNotificationRedirectPath(parentTable, parentId) {
  const key = normalizeParentTable(parentTable);
  if (!key) return null;
  if (key === "echo") return "/dashboard";

  const build = DETAIL_PATH[key];
  if (!build) return null;

  // List-only destinations (activities/tasks) do not need an id.
  if (key === "activity" || key === "task") return build();

  if (parentId == null || parentId === "") return null;
  return build(parentId);
}
